import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculatePnL } from '../../lib/calculations';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { formatCurrencyCompact } from '../../lib/formatters';
import { FEATURE_NAMES } from '../../data/conjointData';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { useMemo, useState } from 'react';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Layers, BarChart3 } from 'lucide-react';

interface Props { selectedPrice: number; }

export function RevenueWaterfall({ selectedPrice }: Props) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<'single' | 'portfolio'>('single');

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Revenue Waterfall</h3>
          <InfoButton helpKey="revenueWaterfall" size={13} />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMode('single')}
            className={`flex items-center gap-1 px-2 py-1 text-micro font-medium rounded transition-colors ${mode === 'single' ? 'bg-chart-teal/10 text-chart-teal' : 'text-muted hover:text-secondary'}`}
          >
            <BarChart3 size={10} /> Single
          </button>
          <button
            onClick={() => setMode('portfolio')}
            className={`flex items-center gap-1 px-2 py-1 text-micro font-medium rounded transition-colors ${mode === 'portfolio' ? 'bg-chart-teal/10 text-chart-teal' : 'text-muted hover:text-secondary'}`}
          >
            <Layers size={10} /> Portfolio
          </button>
        </div>
      </div>

      <p className="text-sm text-muted mb-3">
        {mode === 'single'
          ? 'Single-product P&L waterfall at the selected price point with all 7 features enabled. Use the Builder tab for your actual package configurations.'
          : 'Portfolio waterfall showing per-package contributions and shared fixed costs.'}
      </p>

      {mode === 'single'
        ? <SingleWaterfall selectedPrice={selectedPrice} isMobile={isMobile} />
        : <PortfolioWaterfall isMobile={isMobile} />
      }
    </div>
  );
}

function SingleWaterfall({ selectedPrice, isMobile }: { selectedPrice: number; isMobile: boolean }) {
  const assumptions = useSimulatorStore(s => s.assumptions);

  const data = useMemo(() => {
    const pnl = calculatePnL({ name: 'temp', price: selectedPrice, paymentTerm: 'monthlyZeroApr', enabledFeatures: [...FEATURE_NAMES] }, assumptions);
    return [
      { name: 'Gross Revenue', value: pnl.grossRevenue, type: 'revenue' },
      { name: 'Deductions', value: -(pnl.grossRevenue - pnl.netRevenue), type: 'cost' },
      { name: 'S1 Acquisition', value: -pnl.s1AcquisitionCosts, type: 'cost' },
      { name: 'S2 Rep Cost', value: -pnl.s2RepCosts, type: 'cost' },
      { name: 'Feature COGS', value: -pnl.featureCOGS, type: 'cost' },
      { name: 'Fixed Costs', value: -pnl.fixedCosts, type: 'cost' },
      { name: 'Net P&L', value: pnl.monthlyPnL, type: pnl.monthlyPnL >= 0 ? 'profit' : 'loss' },
    ];
  }, [selectedPrice, assumptions]);

  const colorMap: Record<string, string> = { revenue: CHART_COLORS.teal, cost: CHART_COLORS.red, profit: CHART_COLORS.emerald, loss: CHART_COLORS.red };

  return (
    <>
      <p className="text-sm text-muted mb-4">All features ON at selected price (single-product, full fixed costs)</p>
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="name" {...X_AXIS_PROPS} tick={{ ...X_AXIS_PROPS.tick, fontSize: 11 }} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatCurrencyCompact(v)} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => [formatCurrencyCompact(value), '']} />
          <ReferenceLine y={0} stroke="var(--color-reference-line)" />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
            {data.map((entry, i) => <Cell key={i} fill={colorMap[entry.type]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}

function PortfolioWaterfall({ isMobile }: { isMobile: boolean }) {
  const portfolio = usePortfolioPnL();
  const packages = useSimulatorStore(s => s.packages);

  const data = useMemo(() => {
    const items: { name: string; value: number; type: string }[] = [];

    portfolio.packages.forEach((pkg, i) => {
      items.push({
        name: packages[i]?.name ?? `Pkg ${i + 1}`,
        value: pkg.netRevenueSplit,
        type: 'revenue',
      });
    });

    const totalS1Acq = portfolio.packages.reduce((s, p) => s + p.s1AcquisitionCostsSplit, 0);
    const totalS2Rep = portfolio.packages.reduce((s, p) => s + p.s2RepCostsSplit, 0);
    const totalCOGS = portfolio.packages.reduce((s, p) => s + p.featureCOGSSplit, 0);

    items.push({ name: 'S1 Acquisition', value: -totalS1Acq, type: 'cost' });
    items.push({ name: 'S2 Rep Cost', value: -totalS2Rep, type: 'cost' });
    items.push({ name: 'Feature COGS', value: -totalCOGS, type: 'cost' });
    items.push({ name: 'Fixed Costs', value: -portfolio.fixedCosts, type: 'cost' });
    items.push({
      name: 'Portfolio P&L',
      value: portfolio.portfolioPnLSplit,
      type: portfolio.portfolioPnLSplit >= 0 ? 'profit' : 'loss',
    });

    return items;
  }, [portfolio, packages]);

  const colorMap: Record<string, string> = { revenue: CHART_COLORS.teal, cost: CHART_COLORS.red, profit: CHART_COLORS.emerald, loss: CHART_COLORS.red };

  return (
    <>
      <p className="text-sm text-muted mb-4">Portfolio waterfall (split demand, fixed costs once across all packages)</p>
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="name" {...X_AXIS_PROPS} tick={{ ...X_AXIS_PROPS.tick, fontSize: 10 }} interval={0} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatCurrencyCompact(v)} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => [formatCurrencyCompact(value), '']} />
          <ReferenceLine y={0} stroke="var(--color-reference-line)" />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={50}>
            {data.map((entry, i) => <Cell key={i} fill={colorMap[entry.type]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}
